import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { defer, forkJoin } from 'rxjs';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { EmployeeInjuryIllnessService } from './services/employee-injury-illness.service';
import { EmployeeInjuryIllness, EmployeeInjuryIllnessVerbose } from './models/employee-injury-illness.model';
import { api_routes, db_tables } from '@app/consts';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';
import { ActivatedRoute } from '@angular/router';
import { table } from 'console';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';

@Component({
  selector: 'app-employee-injury-illness',
  templateUrl: './employee-injury-illness.component.html',
  styleUrls: ['./employee-injury-illness.component.scss']
})
export class EmployeeInjuryIllnessComponent implements OnInit {
  @Input() formIds: string[];
  employeeID: string;

  public columns: any[] = [
    { field: "occurenceType", subField: "text", title: "occurence Type", type: "navigation", tableId: "tfi_IiOccurenceType" },
    { field: "occurenceCountry", subField: "name", title: "occurence Country", tableId: "tfi_IiOccurenceCountry" },
    { field: "dateReported", title: "Date Reported", type: "date", tableId: "tfi_IiDateReported" },
    { field: "daysOffWork", title: "Days Off Work", tableId: "tfi_IiDaysOffWork" },
    { field: "leaveStart", title: "Leave Start", type: "date", tableId: "tfi_IiLeaveStart" },
    { field: "leaveEnd", title: "Leave End", type: "date", tableId: "tfi_IiLeaveEnd" },
    { field: "comments", title: "Comments", tableId: "tfi_IiComments" },
  ];

  public filterCategories: any[] = [
    {field: 'occurenceType.text', title: 'Occurence Type', dataType: 'String', tableId: "tfi_IiOccurenceType"},
    {field: 'occurenceCountry.id', title: 'Occurence Country', dataType: "SpecialLookup", lookupCode: "country", tableId: "tfi_IiOccurenceCountry"},
    {field: 'dateReported', title: 'Date Reported', dataType: 'Date', tableId: "tfi_IiDateReported"},
    {field: 'daysOffWork', title: 'Days Off Work', dataType: 'Number', tableId: "tfi_IiDaysOffWork"},
    {field: 'leaveStart', title: 'Leave Start', dataType: 'Date', tableId: "tfi_IiLeaveStart"},
    {field: 'leaveEnd', title: 'Leave End', dataType: 'Date', tableId: "tfi_IiLeaveEnd"},
    {field: 'comments', title: 'Comments', dataType: 'String', tableId: "tfi_IiComments"},
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: MatDialogRef<FormGeneratorDialogComponent>;
  formId: string = 'frm_Is8N1cN544Kf2L'
  changeReasonFormId: string = 'frm_BuUOpoaVa7iQgF';
  formValid: boolean;
  private sortString: string = 'dateReported-desc';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.INJURY_ILLNESS}/[entityId]`;
  filterString: string;
  getRequest: any;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private employeeInjuryIllnesService: EmployeeInjuryIllnessService,
    private snackbarService: SnackbarService,
    private tablePermissionsService: TablePermissionsService,
    private overlayService: OverlayService,
    private route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(
      params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.getInjuryIllnesss();
    this.getTargetEmployeeTablePermissions();
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if(currentFetchedData !== null){
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
      .pipe(
        finalize(() => {
            this.isLoadingPermissions = false;
        })
      )
      .subscribe(
        res => {
          this.targetEmployeeTablePermissions = res;
        }
      );
    }
  }

  getInjuryIllnesss() {
    this.isLoading = true;

    this.getRequest = this.employeeInjuryIllnesService.getInjuryIllnesses(this.employeeID, this.pageSize, this.skip, this.sortString, this.filterString)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
        }
      );
  }

  getInjuryIllness(injuryIllness?: EmployeeInjuryIllness) {
    if(injuryIllness === undefined){
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.employeeInjuryIllnesService.getInjuryIllness(this.employeeID, injuryIllness.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        res => {
          this.openFormDialog(res);
        }
      );
    }
  }

  openFormDialog(injuryIllness?: EmployeeInjuryIllnessVerbose) {
    let formData = {
      id: injuryIllness ? injuryIllness.id : null,
      asOf: moment().format(),
      status: injuryIllness && injuryIllness.status ? injuryIllness.status.id : null,
      governmentReportable: injuryIllness && injuryIllness.governmentReportable ? injuryIllness.governmentReportable.id : null,
      caseDescription: injuryIllness && injuryIllness.caseDescription ? injuryIllness.caseDescription.id : null,
      dayOfWeek: injuryIllness && injuryIllness.dayOfWeek ? injuryIllness.dayOfWeek.id : null,
      occurenceLocation: injuryIllness && injuryIllness.occurenceLocation ? injuryIllness.occurenceLocation.id : null,
      occuredOnEmployeePremise: injuryIllness && injuryIllness.occuredOnEmployeePremise ? injuryIllness.occuredOnEmployeePremise.id : null,
      partOfBody: injuryIllness && injuryIllness.partOfBody ? injuryIllness.partOfBody.id : null,
      occurenceType: injuryIllness && injuryIllness.occurenceType ? injuryIllness.occurenceType.id : null,
      occupationalIllness: injuryIllness && injuryIllness.occupationalIllness ? injuryIllness.occupationalIllness.id : null,
      occurenceCountry: injuryIllness && injuryIllness.occurenceCountry ? injuryIllness.occurenceCountry.id : null,
      dateReported: injuryIllness ? injuryIllness.dateReported : null,
      leaveStart: injuryIllness ? injuryIllness.leaveStart : null,
      leaveEnd: injuryIllness ? injuryIllness.leaveEnd : null,
      injuryIllnessDate: injuryIllness ? injuryIllness.injuryIllnessDate : null,
      timeOfIncident: injuryIllness ? injuryIllness.timeOfIncident : null,
      daysOffWork: injuryIllness ? injuryIllness.daysOffWork : null,
      comments: injuryIllness ? injuryIllness.comments : null,
      changeReason: '',
      changeReasonComments: ''
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('InjuryIllness')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any){
    this.overlayService.show();

    if(!formData.id){
      this.employeeInjuryIllnesService.createInjuryIllness(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getInjuryIllnesss();
        }
      );
    }
    else {
      this.employeeInjuryIllnesService.updateInjuryIllness(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getInjuryIllnesss();
        }
      );
    }
  }

  deleteInjuryIllnesses(InjuryIllnessIds: string[]) {
    const observables = InjuryIllnessIds.map(selectedItem => defer(() => this.employeeInjuryIllnesService.deleteInjuryIllness(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.getInjuryIllnesss();
        this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.getInjuryIllnesss();
  }

  gridStateChange(gridState: GridState) {
      if(this.pageSize !== gridState.pageSize) {
        this.pageSize = gridState.pageSize;
      }
      if(this.skip !== gridState.skip) {
        this.skip = gridState.skip;
      }
      if(this.sortString !== gridState.sortString) {
        this.sortString = gridState.sortString;
      }
      if(this.filterString !== gridState.filter) {
        this.filterString = gridState.filter;
        this.skip = 0;
      }
  
      this.getRequest.unsubscribe();
      this.getInjuryIllnesss();
    }

}




